import {
    Box,
    Button,
    Collapse,
    FormErrorMessage,
    Text,
    Textarea,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Layout } from 'components/atoms'
import { ModalBuild } from 'components/molecules'
import { FormControlCustom } from 'components/molecules/InputBuild/styles'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { forwardRef, useImperativeHandle, useRef } from 'react'
import { useForm } from 'react-hook-form'
import ContractService from 'services/contracts'
import * as yup from 'yup'

export interface ModalCancelContractHandle {
    openModal: () => void
}

interface ModalCancelProps {
    contractId: string
    operationContract: number
    setDisableAllButton: (disable: boolean) => void
}

export const ModalCancelContract = forwardRef<
    ModalCancelContractHandle,
    ModalCancelProps
>(({ contractId, operationContract, setDisableAllButton }, ref) => {
    const modalRef = useRef<ModalBuildHandle>(null)

    const schema = yup.object().shape({
        motivo: yup
            .string()
            .max(50, 'O Motivo deve ser no máximo 50 caracteres')
            .required('Motivo é um campo obrigatório')
    })

    const toast = useToast()
    const {
        handleSubmit,
        register,
        watch,
        reset,
        formState: { isValid, errors }
    } = useForm<any>({
        resolver: yupResolver(schema),
        mode: 'onChange'
    })

    const fetchChangeStatus = async () => {
        const title = operationContract === 1 ? 'Cancelar' : 'Baixar'
        try {
            await ContractService.cancelContract({
                id: contractId,
                body: {
                    motivo: watch('motivo') || '',
                    tipoOperacao: operationContract
                }
            })
            toast({
                title: `Sucesso ao ${title} Contrato`,
                description:
                    'Verifique o status de cada contrato na aba contratos.',
                status: 'success'
            })

            reset()
            setDisableAllButton(true)
        } catch (error) {
            toast({
                title: `Erro ao ${title} Contrato`,
                description: 'Tente novamente mais tarde.',
                status: 'error'
            })
        }
    }

    const openModal = (): void => {
        modalRef.current?.onOpen()
    }

    useImperativeHandle(ref, () => ({
        openModal
    }))

    const closeModal = (): void => {
        modalRef.current?.onClose()
    }

    const handleConfirm = () => {
        if (!isValid) return
        closeModal()
        fetchChangeStatus()
    }

    const handleCancel = () => {
        closeModal()
    }

    return (
        <ModalBuild ref={modalRef} title=" ">
            <Box px={6}>
                <form onSubmit={handleSubmit(handleConfirm)} noValidate>
                    <Text mb={4}>
                        Deseja confirmar{' '}
                        {operationContract === 1
                            ? 'o cancelamento '
                            : 'a baixa '}
                        do contrato?
                    </Text>

                    <FormControlCustom
                        isInvalid={!!errors.motivo?.message}
                        isRequired={true}
                    >
                        <Textarea
                            id="motivo"
                            maxLength={50}
                            placeholder="Descreva o motivo"
                            {...register('motivo')}
                            value={watch('motivo') as string}
                            required={true}
                        />
                        <Collapse in={!!errors.motivo?.message} animateOpacity>
                            <Box minH="15px">
                                <FormErrorMessage
                                    position="relative"
                                    mt={1}
                                    fontSize="xs"
                                    justifyContent="end"
                                >
                                    {errors.motivo?.message as any}
                                </FormErrorMessage>
                            </Box>
                        </Collapse>
                    </FormControlCustom>

                    <Layout type="row" paddingTop={6}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="end"
                            gap={6}
                        >
                            <Button
                                colorScheme="primary"
                                variant="outline"
                                onClick={handleCancel}
                            >
                                Voltar
                            </Button>
                            <Button
                                data-testid="form-action-confirm"
                                colorScheme="primary"
                                type="submit"
                            >
                                Confirmar
                            </Button>
                        </Box>
                    </Layout>
                </form>
            </Box>
        </ModalBuild>
    )
})